import React, { useState, useMemo } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EnvelopeInfo, EnvelopeParam } from '../types';
import { TRANSACTION_DESCRIPTIONS } from '../constants';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EnvelopesSummaryProps {
    title: string;
    envelopes: EnvelopeInfo[];
    taskState: string;
    locked?: boolean;
    lockedMessage?: string;
    emptyMessage?: string;
    runningState: string;
    completedState: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SPACING = {
    betweenCards: 3,
    withinCard: 2,
    betweenEqual: 1,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getEnvelopeStatusVariant = (version: number): any => {
    if (version === 1) return 'success';
    if (version > 1) return 'warning';
    return 'success';
};

const getEnvelopeLabelVariant = (version: number): string => {
    if (version === 1) return 'New envelope';
    if (version > 1) return 'Modified envelope';
    return 'New envelope';
};

const getTransactionDescription = (transactionCode: number): string =>
    TRANSACTION_DESCRIPTIONS[transactionCode] || `Transaction ${transactionCode}`;

const isEmpty = (value: string | null): boolean => !value || value === '-' || value === '—';

const getChangeStatus = (param: EnvelopeParam): { label: string; color: any } => {
    const prevEmpty = isEmpty(param.previous_value);
    const newEmpty = isEmpty(param.param_value);
    if (prevEmpty && !newEmpty) return { label: 'Addition', color: 'success' };
    if (!prevEmpty && newEmpty) return { label: 'Deletion', color: 'error' };
    if (param.previous_value === param.param_value) return { label: 'No Changes', color: 'passive' };
    return { label: 'Change', color: 'warning' };
};


const EnvelopesSummary: React.FC<EnvelopesSummaryProps> = ({
    title,
    envelopes,
    taskState,
    locked = false,
    lockedMessage = 'Complete previous stage to unlock',
    emptyMessage = 'Data will be available once the previous stage is completed.',
    runningState,
    completedState,
}) => {
    const defaultExpanded = useMemo(() => {
        if (!envelopes?.length) return null;
        return envelopes[0];
    }, [envelopes]);

    const [expandedEnvelope, setExpandedEnvelope] = useState<EnvelopeInfo | null>(defaultExpanded);

    const hasNoData = !envelopes || envelopes.length === 0;

    if (locked || hasNoData) {
        return (
            <Accordion
                defaultExpanded={false}
                sx={{
                    borderRadius: '8px !important',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1) !important',
                    border: 'none',
                    '&:before': { display: 'none' },
                    '&.MuiAccordion-root': { border: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                        <Typography variant="h2" sx={{ color: 'rgba(0,0,0,0.87)' }}>{title}</Typography>
                        <Typography variant="bodySmall" color="text.secondary">
                            {lockedMessage}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="bodyMedium" color="text.secondary">
                        {emptyMessage}
                    </Typography>
                </AccordionDetails>
            </Accordion>
        );
    }

    const handleEnvelopeClick = (id: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded) {
            const envelope = envelopes.find((env) => env.envelope_id === id);
            setExpandedEnvelope(envelope || null);
        } else {
            setExpandedEnvelope(null);
        }
    };
    
        return (
            <Box sx={{ position: 'relative' }}>
                {locked && (
                    <Box sx={{
                        position: 'absolute', inset: 0, zIndex: 1,
                        backgroundColor: 'rgba(255,255,255,0.6)',
                        borderRadius: '8px',
                        cursor: 'not-allowed',
                    }} />
                )}
                <Accordion
                    defaultExpanded={!locked}
                    sx={{
                        borderRadius: '8px !important',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1) !important',
                        border: 'none',
                        '&:before': { display: 'none' },
                        '&.MuiAccordion-root': { border: 'none' },
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                            <Typography variant="h2" sx={{ color: 'rgba(0,0,0,0.87)' }}>{title}</Typography>
                            {!locked && (
                                <Chip
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            {taskState === runningState ? 'Deploying' : 'Deployed'}
                                            {taskState === runningState && (
                                                <CircularProgress size={11} sx={{ color: 'inherit' }} />
                                            )}
                                        </Box>
                                    }
                                    size="small"
                                    color={taskState === runningState ? 'informational' : 'success'}
                                    variant="badge"
                                />
                            )}
    
                        </Box>
                    </AccordionSummary>
    
                    <AccordionDetails sx={{ p: SPACING.betweenCards }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: SPACING.withinCard }}>
                            {envelopes.map((envelope: EnvelopeInfo) => (
                                <Accordion
                                    key={envelope.envelope_id}
                                    expanded={expandedEnvelope?.envelope_id === envelope.envelope_id}
                                    onChange={handleEnvelopeClick(envelope.envelope_id)}
                                    disableGutters
                                    sx={{
                                        border: '1px solid #d2d6db',
                                        borderRadius: '8px !important',
                                        boxShadow: 'none',
                                        '&:before': { display: 'none' },
                                        overflow: 'hidden',
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            px: SPACING.withinCard,
                                            py: 0.5,
                                            '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="labelLarge" component="div" sx={{ display: 'block' }}>
                                                    {envelope.envelope_name}
                                                </Typography>
                                                <Typography variant="labelSmall" component="div" color="text.secondary" sx={{ display: 'block' }}>
                                                    {envelope.transaction_type} - {getTransactionDescription(envelope.transaction_type)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, ml: 'auto' }}>
                                                <Chip
                                                    label={getEnvelopeLabelVariant(envelope.current_version)}
                                                    size="small"
                                                    color={getEnvelopeStatusVariant(envelope.current_version)}
                                                    variant="badge"
                                                />
                                                <Typography variant="bodyMedium" color="text.secondary">
                                                    {envelope.params.length ? `${envelope.params.length} modifications` : 'No modifications'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>
    
                                    <AccordionDetails sx={{ px: 0, py: 0, backgroundColor: '#fcfcfc' }}>
                                        <Box sx={{ px: SPACING.withinCard, py: SPACING.withinCard }}>
                                            <Box sx={{ display: 'flex', pb: 1.5, color: '#1f1f1f', fontWeight: 700 }}>
                                                <Box sx={{ flexBasis: '34%', maxWidth: '34%' }}>
                                                    <Typography variant="labelMedium">&nbsp;</Typography>
                                                </Box>
                                                <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                    <Typography variant="labelMedium">Previous Value</Typography>
                                                </Box>
                                                <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                    <Typography variant="labelMedium">New Value</Typography>
                                                </Box>
                                                <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                    <Typography variant="labelMedium">Changes</Typography>
                                                </Box>
                                            </Box>
                                            <Divider />
                                            {envelope.params.map((param: EnvelopeParam) => {
                                                return (
                                                    <React.Fragment key={`${envelope.envelope_id}-${param.param_name}`}>
                                                        <Box sx={{ display: 'flex', py: 1.5, alignItems: 'center' }}>
                                                            <Box sx={{ flexBasis: '34%', maxWidth: '34%' }}>
                                                                <Typography variant="bodyMedium">{param.param_name}</Typography>
                                                            </Box>
                                                            <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                                <Typography variant="bodyMedium">
                                                                    {param.previous_value}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                                <Typography variant="bodyMedium">
                                                                    {param.param_value}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                                <Chip
                                                                    label={getChangeStatus(param).label}
                                                                    color={getChangeStatus(param).color}
                                                                    size="small"
                                                                    variant="badge"
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Divider />
                                                    </React.Fragment>
                                                );
                                            })}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Box>
        );
};

export default EnvelopesSummary;